import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { useFamiliarity } from '../../hooks/useFamiliarity';
import { useAuth } from '../../hooks/useAuth';
import { StockCategoryFilter } from '../../types/stockCategories';
import { cn } from '../../utils/cn';

interface FamiliarityPollProps {
  symbol: string;
  companyName?: string;
  category?: StockCategoryFilter;
  gameDate?: string;
}

export const FamiliarityPoll: React.FC<FamiliarityPollProps> = ({
  symbol,
  companyName,
  category,
  gameDate,
}) => {
  const { user } = useAuth();
  const isAuthenticated = Boolean(user);

  const { stats, isLoading, isVoting, hasVoted, submitVote } = useFamiliarity({
    symbol,
    category,
    gameDate,
    enabled: true,
    isAuthenticated,
  });

  const yesPercent = stats.total > 0 ? Math.round((stats.yesCount / stats.total) * 100) : 0;
  const displayName = companyName || symbol;

  return (
    <div className="space-y-3 border-t border-border/60 pt-6 text-left">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Company Familiarity
      </p>

      {!hasVoted ? (
        <>
          <p className="text-sm font-medium text-foreground">
            Had you heard of {displayName} before today?
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isVoting}
              onClick={() => submitVote(true)}
              className="flex-1 rounded-xl border-2 border-emerald-500/60 text-emerald-600 dark:text-emerald-200 hover:bg-emerald-500/10 py-2.5 font-semibold"
            >
              {isVoting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isVoting}
              onClick={() => submitVote(false)}
              className="flex-1 rounded-xl border-2 border-rose-500/60 text-rose-500 dark:text-rose-200 hover:bg-rose-500/10 py-2.5 font-semibold"
            >
              {isVoting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'No'}
            </Button>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            {yesPercent}% of players had heard of {displayName}
            {stats.total > 0 && (
              <span className="text-muted-foreground font-normal">
                {' '}({stats.total} vote{stats.total !== 1 ? 's' : ''})
              </span>
            )}
          </p>
          {/* Progress bar */}
          <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500',
                yesPercent >= 50
                  ? 'bg-emerald-500'
                  : 'bg-rose-500'
              )}
              style={{ width: `${Math.max(yesPercent, 2)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Yes {yesPercent}%</span>
            <span>No {100 - yesPercent}%</span>
          </div>
        </div>
      )}

      {isLoading && !hasVoted && (
        <div className="flex justify-center py-1">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

export default FamiliarityPoll;
